import { ChevronLeft } from "lucide-react";
import React, { useState, ChangeEvent, useEffect } from "react";
import { VideoBlog } from "../components/VideoBlogs";
import toast from "react-hot-toast";
import { BlogService } from "../services/api/blog.service";
interface FormData {
  title: string;
  category: string;
  description: string;
  video: File | null;
}
interface props {
  onClose: () => void;
  blogData: VideoBlog | null;
  handleAddVideoSuccess: () => void;
}

const VideoForm: React.FC<props> = ({ onClose, blogData, handleAddVideoSuccess }) => {
  const [formData, setFormData] = useState<FormData>({
    title: "",
    category: "",
    description: "",
    video: null,
  });

  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (blogData) {
      setFormData({
        title: blogData.title,
        category: blogData.category,
        description: blogData.description,
        video: null,
      });
      setVideoPreview(blogData.url);
    } else {
      setFormData({
        title: "",
        category: "",
        description: "",
        video: null,
      });
      setVideoPreview(null);
    }
  }, [blogData]);
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleVideoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, video: file }));
      setVideoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    console.log("Form submitted:", formData);
    if (submitting) return;
    

    // Validate required fields
    if (!formData.title || !formData.description || !formData.category) {
      toast.error("Please fill all the details");
      return;
    }

    if (!formData.video) {
      toast.error("Please upload a video file");
      return; // ⬅️ Important: you must return here
    }

    const data = new FormData();

    data.append("title", formData.title);
    data.append("category", formData.category);
    data.append("description", formData.description);

    if (formData.video instanceof File) {
      data.append("video", formData.video);
    } else {
      toast.error("Invalid video file");
      return;
    }

    try {
      setSubmitting(true);
      const response = await BlogService.addVideo(data);
      if (response.success) {
        toast.success("Video added successfully");
        handleAddVideoSuccess();
      } else {
        toast.error("Failed to add video");
      }
    } catch (error) {
      console.error("Error adding video:", error);
      toast.error("Failed to add video");
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 p-2 w-full">
      <div className="flex flex-row gap-2 pb-2 items-center">
        <button onClick={onClose}><ChevronLeft size={24} /></button>
        <h2 className="text-xl font-semibold text-green-600 flex items-center">Add Video Blog</h2>
      </div>
      <div className="w-full bg-white shadow-lg rounded-2xl p-6">

        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Form Fields */}
            <div className="space-y-4 items-center">
              {/* Title Field */}
              <div>
                <label className="block text-gray-900 font-medium mb-1">Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter video title"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-600"
                  required
                />
              </div>

              {/* Category Field */}
              <div>
                <label className="block text-gray-900 font-medium mb-1">
                  Category
                </label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  placeholder="Enter category"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-600"
                  required
                />
              </div>

              {/* Description Field */}
              <div>
                <label className="block text-gray-900 font-medium mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter video description"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-600 resize-none"
                  rows={4}
                />
              </div>

              {/* Submit Button */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="w-[40%] bg-green-600 text-white py-2 px-4 rounded-md font-semibold hover:bg-green-700 transition"
              >
                {submitting?'Submitting...':'Submit'}
              </button>
            </div>

            {/* Right Column - Video Upload */}
            <div className="space-y-4">
              <div>
                <label className="block text-gray-900 font-medium mb-1">
                  Upload Video
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-600 transition">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoChange}
                    className="block w-full text-gray-600 file:mr-4 file:py-2 file:px-4
                         file:rounded-md file:border-0 file:text-sm
                         file:font-semibold file:bg-green-600 file:text-white
                         hover:file:bg-green-700 file:cursor-pointer cursor-pointer"
                  />
                </div>
              </div>

              {/* Video Preview */}
              {videoPreview && (
                <div>
                  <p className="text-gray-900 font-medium mb-2">Preview:</p>
                  <video
                    src={videoPreview}
                    controls
                    className="w-full h-64 rounded-lg border border-gray-300"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoForm;